import { useState } from 'react';
import { mockUsers, AUTH_PASSWORD, type User } from '../lib/mockData';

interface LoginModalProps {
  onLogin: (user: User) => void;
}

type UserRole = 'student' | 'teacher' | null;

export function LoginModal({ onLogin }: LoginModalProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Simulate a small delay for better UX
    setTimeout(() => {
      // Find user by username
      const user = mockUsers.find(u => u.username.toLowerCase() === username.toLowerCase());
      
      if (!user) {
        setError('Username not found. Please check your username.');
        setIsLoading(false);
        return;
      }

      // Check if user role matches selected role
      if (user.role !== selectedRole) {
        setError(`This username is registered as a ${user.role}, not a ${selectedRole}.`);
        setIsLoading(false);
        return;
      }

      // Check password
      if (password !== AUTH_PASSWORD) {
        setError('Incorrect password. Please try again.');
        setIsLoading(false);
        return;
      }

      // Success - login user
      onLogin(user);
      setIsLoading(false);
    }, 500);
  };

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setError('');
    setUsername('');
    setPassword('');
  };

  const handleBack = () => {
    setSelectedRole(null);
    setError('');
    setUsername('');
    setPassword('');
  };

  // Role Selection Screen
  if (!selectedRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-green-600 dark:from-blue-700 dark:to-green-700 px-8 py-6 text-white">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white dark:bg-gray-700 rounded-full p-3">
                <span className="text-4xl">📚</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-center mb-2">Open Class Ug</h1>
            <p className="text-center text-blue-100 dark:text-gray-300">Digital Learning Platform</p>
          </div>

          {/* Role Selection */}
          <div className="p-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white text-center">
              I am a...
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-400 mb-8">
              Select your role to continue
            </p>

            <div className="space-y-4">
              {/* Student Button */}
              <button
                onClick={() => handleRoleSelect('student')}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 dark:from-blue-700 dark:to-blue-800 dark:hover:from-blue-600 dark:hover:to-blue-700 text-white p-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <div className="flex items-center justify-center space-x-4">
                  <span className="text-5xl">👨‍🎓</span>
                  <div className="text-left">
                    <h3 className="text-xl font-bold">Student</h3>
                    <p className="text-sm text-blue-100">Access learning materials and submit assignments</p>
                  </div>
                </div>
              </button>

              {/* Teacher Button */}
              <button
                onClick={() => handleRoleSelect('teacher')}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 dark:from-green-700 dark:to-green-800 dark:hover:from-green-600 dark:hover:to-green-700 text-white p-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <div className="flex items-center justify-center space-x-4">
                  <span className="text-5xl">👨‍🏫</span>
                  <div className="text-left">
                    <h3 className="text-xl font-bold">Teacher</h3>
                    <p className="text-sm text-green-100">Upload content and manage assignments</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login Form Screen
  const roleColor = selectedRole === 'student' ? 'blue' : 'green';
  const roleIcon = selectedRole === 'student' ? '👨‍🎓' : '👨‍🏫';
  const roleLabel = selectedRole === 'student' ? 'Student' : 'Teacher';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${roleColor}-600 to-${roleColor}-700 dark:from-${roleColor}-700 dark:to-${roleColor}-800 px-8 py-6 text-white`}
             style={{
               background: selectedRole === 'student' 
                 ? 'linear-gradient(to right, rgb(37, 99, 235), rgb(29, 78, 216))'
                 : 'linear-gradient(to right, rgb(22, 163, 74), rgb(21, 128, 61))'
             }}>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white dark:bg-gray-700 rounded-full p-3">
              <span className="text-4xl">{roleIcon}</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-2">Open Class Ug</h1>
          <p className="text-center text-blue-100 dark:text-gray-300">{roleLabel} Login</p>
        </div>

        {/* Login Form */}
        <div className="p-8">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="mb-4 flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm">Back to role selection</span>
          </button>

          <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white text-center">
            Sign In as {roleLabel}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                placeholder={`Enter your ${roleLabel.toLowerCase()} username`}
                required
                disabled={isLoading}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="Enter your password"
                required
                disabled={isLoading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 dark:from-blue-700 dark:to-green-700 dark:hover:from-blue-600 dark:hover:to-green-600 text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Helper Text */}
          <div className={`mt-6 p-4 bg-${roleColor}-50 dark:bg-gray-700 rounded-lg`}
               style={{
                 backgroundColor: selectedRole === 'student' 
                   ? 'rgb(239, 246, 255)'
                   : 'rgb(240, 253, 244)'
               }}>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Demo {roleLabel} Users:</strong>
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              {selectedRole === 'student' 
                ? 'alice, brian, christine, david, emma'
                : 'mr.john, mrs.mary, mr.patrick, ms.sarah'
              }
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              <strong>Password for all users:</strong> 123
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
